<template>
  <div>
    <validation-observer ref="observer" tag="fieldset" v-slot="{ invalid }">
      <!-- Type of fee -->
      <b-row class="mt-3">
        <b-col>
          <distribution-form-group
            vid="feeType"
            ref="feeType"
            :label="$t('type_of_fee') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
          >
            <b-form-select required v-model="fee.type" :options="feeOptions" :state="state"></b-form-select>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Effective Calendar Start -->
      <b-row class="mt-2" v-if="feeType === feeStatuses.INTERNATIONAL">
        <b-col>
          <distribution-form-group
            vid="effectiveStart"
            ref="effectiveStart"
            :label="$t('effective_calendar_start_date') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <date-picker
              :input-attr="{required: true}"
              value-type="format"
              v-model="fee.effectiveStart"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Effective Calendar End -->
      <b-row class="mt-2" v-if="feeType === feeStatuses.INTERNATIONAL">
        <b-col>
          <distribution-form-group
            vid="effectiveEnd"
            ref="effectiveEnd"
            :label="$t('effective_calendar_end_date') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <date-picker
              :input-attr="{required: true}"
              value-type="format"
              v-model="fee.effectiveEnd"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Territory of royalties -->
      <b-row class="mt-2" v-if="feeType === feeStatuses.INTERNATIONAL">
        <b-col>
          <distribution-form-group
            vid="country"
            ref="country"
            :label="$t('territory_of_incoming_royalties') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-form-select required v-model="fee.country" :options="countriesOptions" :state="state"></b-form-select>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Tariff -->
      <b-row class="mt-2" v-if="feeType === feeStatuses.BASE || feeType === feeStatuses.SERVICE">
        <b-col>
          <distribution-form-group
            vid="tariffs"
            ref="tariffs"
            :label="$t('tariff') | capitalize"
            label-cols="2"
            :rules="fee.tariffs.except !=='all'? 'required' : ''"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-input-group class="include-exclude-append">
              <template v-slot:append>
                <include-exclude-selector
                  :options="tariffOptions"
                  :except-string="$t('all_except') | capitalize"
                  :include-string="$t('include_specific') | capitalize"
                  :placeholder="getPlaceholder('tariffs')"
                  label=""
                  label-cols="0"
                  v-model="fee.tariffs"
                  :enable-all="true"
                  selectorCols="col-3 pr-0"
                />
              </template>
              <b-form-select required v-model="fee.tariffs.values" :options="[]" :state="state" :disabled="true" :hidden="true" />
            </b-input-group>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Period Start -->
      <b-row class="mt-2" v-if="feeType === feeStatuses.BASE || feeType === feeStatuses.SERVICE">
        <b-col>
          <distribution-form-group
            vid="periodStart"
            ref="periodStart"
            :label="$t('usage_start_date') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <date-picker
              :input-attr="{required: true}"
              value-type="format"
              v-model="fee.periodStart"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Period End -->
      <b-row class="mt-2" v-if="feeType === feeStatuses.BASE || feeType === feeStatuses.SERVICE">
        <b-col>
          <distribution-form-group
            vid="periodEnd"
            ref="periodEnd"
            :label="$t('usage_end_date') | capitalize"
            label-cols="2"
            rules="required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <date-picker
              :input-attr="{required: true}"
              value-type="format"
              v-model="fee.periodEnd"
              :class="{invalid: state === false}"
              :format="$config.ISO_DATE_FORMAT"
            />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Rightsholder -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            vid="rightsholder"
            ref="rightsholder"
            :label="$t('rightsholder') | capitalize"
            label-cols="2"
            :rules="fee.rightsholder.except !=='all'? 'required' : ''"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-input-group class="custom-select-search">
              <template v-slot:append>
                <b-form-select class="col-3" v-model="fee.rightsholder.except">
                  <b-form-select-option value="all">{{ $t("all") | capitalize }}</b-form-select-option>
                  <b-form-select-option :value="false">{{ $t('include_specific') | capitalize }}</b-form-select-option>
                  <b-form-select-option :value="true">{{ $t('all_except') | capitalize }}</b-form-select-option>
                </b-form-select>

                <multi-select 
                  v-model="fee.rightsholder.values" 
                  id="societyMultiSelectSearch"
                  label="name" 
                  track-by="value" 
                  :placeholder="getPlaceholder('rightsholder')" 
                  :options="rightsholdersOptions" 
                  :multiple="true" 
                  :searchable="true" 
                  :loading="isLoading"
                  :close-on-select="false" 
                  :show-no-results="true" 
                  @search-change="findRightsholders"
                  :disabled="fee.rightsholder.except === 'all'"
                >
                </multi-select>
              </template>
              <b-form-select required v-model="fee.rightsholder.values" :options="[]" :state="state" :disabled="true" :hidden="true" />
            </b-input-group>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Society -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            vid="societies"
            ref="societies"
            :label="$t('society') | capitalize"
            label-cols="2"
            :rules="fee.societies.except !=='all'? 'required' : ''"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-input-group class="include-exclude-append">
              <template v-slot:append>
                <include-exclude-selector
                  :options="memberCollectivesOptions"
                  :except-string="$t('all_except') | capitalize"
                  :include-string="$t('include_specific') | capitalize"
                  :placeholder="getPlaceholder('societies')"
                  label=""
                  label-cols="0"
                  v-model="fee.societies"
                  :enable-all="true"
                  selectorCols="col-3 pr-0"
                />
              </template>
              <b-form-select required v-model="fee.societies.values" :options="[]" :state="state" :disabled="true" :hidden="true" />
            </b-input-group>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Rate -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            vid="rate"
            ref="rate"
            :label="$t('rate') | capitalize"
            label-cols="2"
            rules="double|required"
            v-slot="{ state }"
            :disabled="fee.type === null"
          >
            <b-form-input required v-model="fee.rate" :state="state"></b-form-input>
          </distribution-form-group>
        </b-col>
      </b-row>
      <div class="fixed-footer">
        <div class="d-flex justify-content-between">
          <b-button class="btn-outline-dark" @click="$bvModal.hide('edit-modal')">{{ $t("cancel") | capitalize }}</b-button>
          <b-button
            class="ml-3 btn-outline-red"
            @click="submit"
            :disabled="invalid || !isOriginalFeeModifed"
          >
            {{ $t("save") | capitalize }}
          </b-button>
        </div>
      </div>
    </validation-observer>
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex"
import DistributionFormGroup from "@/components/DistributionFormGroup"
import { ValidationObserver } from "vee-validate"
import { capitalize } from "lodash"
import { feeStatuses } from "@/constants"
import { isEqual } from "lodash"
export default {
  name: "EditFeeForm",
  components: { DistributionFormGroup, ValidationObserver },
  props: {
    feeId: {
      type: String,
      required: true,
    },
    feeType: {
      type: String,
      required: true,
    }
  },
  data () {
    return {
      isLoading: false,
      rightsholdersOptions: [],
      feeStatuses,
      originalFee: {},
      fee: {
        id: null,
        type: null,
        effectiveStart: null,
        effectiveEnd: null,
        country: null,
        tariffs: {
          values: [],
          except: null,
        },
        rightsholder: {
          values: [],
          except: null,
        },
        societies: {
          values: [],
          except: null,
        },
        rate: null,
        periodStart: null,
        periodEnd: null,
      },
      feeOptions: [
        { value: feeStatuses.BASE, text: capitalize(this.$t("base_fees")) },
        { value: feeStatuses.SERVICE, text: capitalize(this.$t("service_fees")) },
        { value: feeStatuses.INTERNATIONAL, text: capitalize(this.$t("international_incoming")) },
      ],
    }
  },
  computed: {
    ...mapState("consts", ["countries", "tariffs", "memberCollectives" ]),
    countriesOptions () {
      let options = this.countries.map(c => {
        return { text: c.text, value: c.alpha3 }
      })
      return options
    },
    tariffOptions () {
      let options = this.tariffs.map(c => {
        return { name: c.text, value: c.code }
      })
      return options
    },
    memberCollectivesOptions () {
      let options = this.memberCollectives.map(c => {
        return { name: c.name, value: c.uuid }
      })
      return options
    },
    isOriginalFeeModifed () {
      return !isEqual(this.fee, this.originalFee)
    }
  },
  methods: {
    ...mapActions("consts", ["getTariffs", "getCountries"]),
    ...mapActions("alert", ["success", "error"]),
    getPlaceholder (key) {
      if (this.fee[key]?.select === "all")
        return ""
      return this.fee[key]?.select ? this.$t("except_placeholder") : this.$t("include_placeholder")
    },
    findRightsholders (query) {
      if (query) {
        this.isLoading = true
        this.$api.rightsholders.rightsholdersList({ search: query }).then(response => {
          this.rightsholdersOptions = response.data.results
            .map(r => {
              return {
                "name": r["name"],
                "value": r["resoundId"],
                "id": r.id,
                "resoundId": r.resoundId,
              }
            })
        }).finally(() => {
          this.isLoading = false
        })
      }
    },
    initFeeData (feeData) {
      this.fee.id = feeData.id
      this.fee.type = feeData.type
      this.fee.rate = feeData.rate
      this.fee.country = feeData.scope.country
      if (feeData.type === feeStatuses.INTERNATIONAL) {
        this.fee.effectiveStart = feeData.scope.period.start
        this.fee.effectiveEnd = feeData.scope.period.end
      } else {
        this.fee.periodStart = feeData.scope.period.start
        this.fee.periodEnd = feeData.scope.period.end
      }
      if (feeData.scope.tariffs.codes) {
        if (feeData.scope.tariffs.codes.length === this.tariffOptions.length) {
          this.fee.tariffs.except = "all"
        } else {
          this.fee.tariffs.except = false
          this.fee.tariffs.values = this.tariffOptions.filter(option => feeData.scope.tariffs.codes.includes(option.value))
        }
      }
      if (feeData.scope.societies.ids) {        
        if (feeData.scope.societies.ids.length === this.memberCollectivesOptions.length) {
          this.fee.societies.except = "all"
        } else {
          this.fee.societies.except = false
          this.fee.societies.values = this.memberCollectivesOptions.filter(option => feeData.scope.societies.ids.includes(option.name))
        }
      }
      if (feeData.scope.rightsHolders) {
        this.fee.rightsholder.except = feeData.scope.rightsHolders.select === "all" ? "all" : feeData.scope.rightsHolders.select === "include" ? false : true
        this.fee.rightsholder.values = feeData.scope.rightsHolders.ids && feeData.scope.rightsHolders.ids.map(val => ({
          id: val.id,
          name: val.name,
          resoundId: val.resoundId,
          value: val.resoundId
        }))
      }
      this.originalFee = structuredClone(this.fee)
    },
    submit () {
      let data = {
        type: this.fee.type,
        rate: this.fee.rate,
        period: {
          start: this.fee.type === feeStatuses.INTERNATIONAL ? this.fee.effectiveStart : this.fee.periodStart,
          end: this.fee.type === feeStatuses.INTERNATIONAL ? this.fee.effectiveEnd : this.fee.periodEnd,
        },
        societies: {
          ids: this.fee.societies.values ? this.fee.societies.values.map(society => society.value) : [],
          select: this.getSelectVal(this.fee.societies.except),
        },
        rightsHolders: {
          ids: this.fee.rightsholder.values ? this.fee.rightsholder.values.map(rightsholder => rightsholder.id) : [],
          select: this.getSelectVal(this.fee.rightsholder.except),
        },
        tariffs: {
          codes: this.fee.tariffs.values ? this.fee.tariffs.values.map(tariff => tariff.value) : [],
          select: this.getSelectVal(this.fee.tariffs.except),
        },
        country: this.fee.country,
      }
      this.$api.fees.updateFee(this.feeId, data).then(() => {
        this.success(capitalize(this.$t("saved_changes")))
        this.$emit("feeUpdated")
      }).catch(error => {
        const { data, status } = error.response
        let message = ""
        if (status === 400) {
          message = "Failed to update Fee. Please correct the data and try again."
          if (data.nonFieldErrors) {
            message = message + "\n" + data.nonFieldErrors.join("\n")
          }
        } else if (status === 422) {
          message = "Failed to update fee. Please correct the data and try again."
          if (data.nonFieldErrors) {
            message = message + "\n" + data.nonFieldErrors.map(e => e.message).join("\n")
          }
        } else {
          message = "Failed to update fee. Please contact administrator or try again."
        }
        this.error(message)
      }) 
    },
    getSelectVal (value) {
      switch (value) {
      case "all": return "all"
      case false: return "include"
      case true: return "exclude"
      default: return null
      }
    },
  },
  mounted () {
    if (this.feeId) {
      this.$api.fees.getFee(this.feeId).then(response => {
        this.initFeeData(response.data)
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-select-search {
  .input-group-append {
    width: stretch;
    column-gap: 20px;
    .custom-select {
      border-radius: 0.25rem;
      background-color: white;
    }
    .select-search-input {
      padding: 0;
    }
    ::v-deep .input-group {
      padding-right: 20px;
    }
    ::v-deep .form-control[readonly] {
      background-color: transparent;
    }
    ::v-deep .form-control[disabled] {
      background-color: transparent;
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: all;
    }
  }
}

.include-exclude-append {
  width: stretch;
  .input-group-append {
    width: stretch;
    .includeExcludeSelector {
      width: stretch;
      column-gap: 20px;
    }
    ::v-deep .custom-select {
      border-radius: 0.25rem;
      background-color: white;
    }
  }
}

::v-deep .multiselect--disabled {
  cursor: not-allowed;
  pointer-events: all;
}

::v-deep .multiselect--disabled .multiselect__select {
    background-color: white !important;
    cursor: not-allowed;
  }

::v-deep fieldset[disabled] {
  cursor: not-allowed;
}

.fixed-footer {
  margin-top: 30px;
  .btn {
    min-width: 108px;
  }
}
</style>